import Cookie from '../utils/Cookie';
import { store } from '../redux/store';
import { io } from 'socket.io-client';
import { setSocketState } from '../redux/slice/socketSlice';
import { obs, ObsEvent } from '../services/observability';
import { isInternalOnlySocketError } from '../utils/internalOnly';


const API_URI = process.env.REACT_APP_API_URL;
let socket = null;
let connectionStartTime = null;
// Fresh-token source for reconnects: Clerk tokens expire in ~60s, so a static auth
// payload made every auto-reconnect after a blip fail with "Token expired" forever.
let tokenProvider = null;
// Dev/QA allowlist rejection: do not reconnect from visibility/App.js.
let siteAccessDenied = false;

// Call pages subscribe so a transport drop ends the local call UI immediately.
// Backend tears the media/LiveAvatar session down on Socket.IO disconnect; silently
// auto-resuming this socket would leave a zombie call with no live avatar session.
const disconnectListeners = new Set();

function getSocket() {
    return socket;
}

function onSocketDisconnect(listener) {
    disconnectListeners.add(listener);
    return () => disconnectListeners.delete(listener);
}

function notifyDisconnectListeners(reason, meta) {
    for (const listener of [...disconnectListeners]) {
        try {
            listener(reason, meta);
        } catch (err) {
            console.warn('[socket] disconnect listener failed:', err);
        }
    }
}

function discardSocketInstance(instance, { allowReconnect = false } = {}) {
    if (!instance) return;
    try {
        if (!allowReconnect && instance.io?.opts) {
            instance.io.opts.reconnection = false;
        }
    } catch (e) { /* ignore */ }
}

function markInternalOnly() {
    if (siteAccessDenied && !socket) {
        window.dispatchEvent(new CustomEvent('tlk:internal-only'));
        return;
    }
    siteAccessDenied = true;
    const denied = socket;
    socket = null;
    discardSocketInstance(denied);
    try {
        denied?.disconnect();
    } catch (e) { /* ignore */ }
    window.dispatchEvent(new CustomEvent('tlk:internal-only'));
    store.dispatch(setSocketState({ user: null, isInitialized: 'failed' }));
}

function clearInternalOnly() {
    siteAccessDenied = false;
}

async function SocketConnect(tokenOrProvider) {
    if (siteAccessDenied) {
        return;
    }
    connectionStartTime = Date.now();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Accept a provider function (preferred) or a plain token string (back-compat).
    tokenProvider = typeof tokenOrProvider === 'function'
        ? tokenOrProvider
        : async () => tokenOrProvider;

    // Never leave a prior instance auto-reconnecting in the background.
    if (socket) {
        const previous = socket;
        socket = null;
        discardSocketInstance(previous);
        try {
            previous.disconnect();
        } catch (e) { /* ignore */ }
    }

    socket = io(API_URI, {
        // Called on EVERY (re)connect attempt so each handshake carries a live token.
        // timezone rides along because the dev backend reads auth.timezone at connect.
        auth: (cb) => {
            Promise.resolve(tokenProvider())
                // Falsy resolves like a throw: `${null}` would send "null" as the JWT.
                .then((token) => cb({ token: token ? `${token}` : '', timezone }))
                .catch(() => cb({ token: '', timezone }));
        },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        // Back off to 10s between attempts so a server restart isn't hammered every second.
        reconnectionDelayMax: 10000,
    });

    // Handlers must use this instance. Sign-in replaces the guest socket, and a late
    // connect on the old one would otherwise read the module socket after it was cleared.
    const current = socket;
    const isActive = () => socket === current;

    current.on('connect', () => {
        if (!isActive()) return;
        const connectionMs = connectionStartTime ? Date.now() - connectionStartTime : null;
        obs.emit(ObsEvent.SOCKET_CONNECTED, {
            connection_ms: connectionMs,
            socket_id: current.id,
            status: 'success',
        });
    });

    current.on('reconnect', (attemptNumber) => {
        if (!isActive()) return;
        console.log('Socket reconnected after', attemptNumber, 'attempts');
        obs.emit(ObsEvent.SOCKET_RECONNECTED, {
            attempt_number: attemptNumber,
            status: 'success'
        });
    });

    current.on('reconnect_attempt', (attemptNumber) => {
        console.log('Socket reconnection attempt:', attemptNumber);
    });

    current.on('reconnect_error', (error) => {
        if (!isActive()) return;
        console.log('Socket reconnection error:', error?.message);
        if (isInternalOnlySocketError(error)) {
            markInternalOnly();
            return;
        }
        store.dispatch(setSocketState({ user: null, isInitialized: 'failed' }));
        obs.emit(ObsEvent.SOCKET_CONNECTION_DROPPED, {
            error_message: error?.message,
            status: 'error'
        });
    });

    current.on('connection_established', (data) => {
        if (!isActive()) return;
        // Set session context for all future observability events
        obs.setSession(data?.user_id, data?.user_name);

        store.dispatch(setSocketState({ user: data, isInitialized: 'connected' }));
    });

    current.on('error', (error) => {
        if (!isActive()) return;
        console.log('socket error', error);
        const message = error?.message || '';

        if (isInternalOnlySocketError(error)) {
            markInternalOnly();
            return;
        }

        // Token expired: rebuild the connection. Prefer the stored provider so the
        // replacement socket also refreshes its token on every future reconnect.
        if (message.includes("Token expired")) {
            const fallbackToken = Cookie.getCookie('__session');
            if (socket) {
                socket.disconnect();
            }
            socket = null;
            SocketConnect(tokenProvider || fallbackToken);
            return;
        }

        // Video token failure has its own UI state used by VideoChat.
        if (message.includes("Failed to generate video token")) {
            store.dispatch(setSocketState({ user: null, isInitialized: 'video_token_faild' }));
            return;
        }

        // Only treat true auth/connection failures as fatal. Application-
        // level errors (e.g. "chat_id is required", "Failed to generate
        // message") should NOT flip the UI to 'failed' — that locks users
        // into the "Connection failed — Reload" overlay even though the
        // socket itself is perfectly healthy.
        const isFatalConnectionError =
            message.includes("Authentication") ||
            message.includes("Invalid token") ||
            message.includes("missing user ID");

        if (isFatalConnectionError) {
            store.dispatch(setSocketState({ user: null, isInitialized: 'failed' }));
            return;
        }

        console.warn('Non-fatal socket app error (ignored for UI state):', message);
    });

    current.on('connect_timeout', (timeout) => {
        if (!isActive()) return;
        console.log('socket connect_timeout', timeout);
        store.dispatch(setSocketState({ user: null, isInitialized: 'timeout' }));
    });

    current.on('connect_error', (error) => {
        if (!isActive()) return;
        console.log('socket connect_error', error);
        if (isInternalOnlySocketError(error)) {
            markInternalOnly();
            return;
        }
        store.dispatch(setSocketState({ user: null, isInitialized: 'failed' }));
        obs.emit(ObsEvent.SOCKET_CONNECTION_DROPPED, {
            error_message: error?.message,
            error_type: 'connect_error',
            status: 'error'
        });
    });

    current.on('disconnect', (reason) => {
        if (!isActive()) return;
        console.log('socket disconnect', reason);

        // 'io client disconnect' and 'io server disconnect' are intentional
        // Others like 'transport close', 'transport error', 'ping timeout' are unexpected
        const isExpected = reason === 'io client disconnect' || reason === 'io server disconnect';
        obs.emit(ObsEvent.SOCKET_DISCONNECTED, {
            reason: reason,
            status: isExpected ? 'info' : 'error'
        });

        store.dispatch(setSocketState({ user: null, isInitialized: 'disconnected' }));

        // Notify call pages before dropping the module reference so they can hang up.
        notifyDisconnectListeners(reason, { expected: isExpected });

        // Always clear the module reference. Backend tears live calls down on disconnect,
        // so retaining this instance for silent Socket.IO resume creates a zombie call UI.
        // Disable auto-reconnect on unexpected drops so MainLayout/App can rebuild a fresh
        // socket with a live token instead of racing an orphaned reconnecting instance.
        socket = null;
        if (!isExpected) {
            discardSocketInstance(current);
        }
    });
}

async function SocketDisconnect() {
    if (socket) {
        const current = socket;
        discardSocketInstance(current);
        current.disconnect();
        socket = null;
    }
}

export default {
    SocketConnect,
    SocketDisconnect,
    socket,
};

export { SocketConnect, SocketDisconnect, socket, getSocket, onSocketDisconnect, markInternalOnly, clearInternalOnly };
